/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Create Developer API Key Modal (Task 10.6.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  X,
  Key,
  ShieldCheck,
  Zap,
  DollarSign,
  Plus,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useApiKeyStore } from '../../stores/useApiKeyStore';

const AVAILABLE_SCOPES = [
  { id: 'text:write', label: 'text:write (Generate longform articles & prompts)', domain: 'text' },
  { id: 'text:read', label: 'text:read (Read documents & templates)', domain: 'text' },
  { id: 'chat:write', label: 'chat:write (Create sessions & stream LLM responses)', domain: 'chat' },
  { id: 'chat:read', label: 'chat:read (Read chat message history)', domain: 'chat' },
  { id: 'images:write', label: 'images:write (Generate Flux/SD images & inpaint)', domain: 'images' },
  { id: 'images:read', label: 'images:read (View asset metadata & gallery items)', domain: 'images' },
  { id: 'audio:write', label: 'audio:write (Generate neural TTS & transcribe Whisper)', domain: 'audio' },
  { id: 'audio:read', label: 'audio:read (Listen to voice samples & read transcripts)', domain: 'audio' },
  { id: 'translations:write', label: 'translations:write (Create CAT segments & sync TM)', domain: 'translations' },
  { id: 'translations:read', label: 'translations:read (Query TM matches & glossary)', domain: 'translations' },
];

export const CreateApiKeyModal: React.FC = () => {
  const { t } = useTranslation();
  const { isCreateModalOpen, setCreateModalOpen, createKey } = useApiKeyStore();

  const [name, setName] = useState('');
  const [environment, setEnvironment] = useState<'live' | 'test'>('live');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([
    'text:write',
    'chat:write',
  ]);
  const [isFullAdmin, setIsFullAdmin] = useState(false);
  const [rateLimitRpm, setRateLimitRpm] = useState<number>(120);
  const [monthlySpendLimit, setMonthlySpendLimit] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isCreateModalOpen) return null;

  const toggleScope = (scopeId: string) => {
    if (isFullAdmin) setIsFullAdmin(false);
    setSelectedScopes((prev) =>
      prev.includes(scopeId) ? prev.filter((s) => s !== scopeId) : [...prev, scopeId]
    );
  };

  const toggleFullAdmin = () => {
    if (!isFullAdmin) {
      setIsFullAdmin(true);
      setSelectedScopes(['*']);
    } else {
      setIsFullAdmin(false);
      setSelectedScopes(['text:write', 'chat:write']);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      const finalScopes = isFullAdmin ? ['*'] : selectedScopes;
      await createKey({
        name: name.trim(),
        environment,
        scopes: finalScopes,
        rateLimitRpm,
        monthlySpendLimit: monthlySpendLimit ? parseFloat(monthlySpendLimit) : null,
      });
      // Modal closes and opens reveal modal inside createKey
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="bg-surface rounded-2xl border border-border shadow-2xl w-full max-w-xl overflow-hidden flex flex-col max-h-[92vh] animate-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-lg bg-primary/10 text-primary">
              <Key className="size-4" />
            </span>
            <div>
              <h3 className="text-sm font-semibold text-text">
                {t('apikeys.createModalTitle')}
              </h3>
              <p className="text-xs text-text-muted">
                {t('apikeys.createModalSubtitle')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setCreateModalOpen(false)}
            className="p-1 rounded-lg hover:bg-surface-subtle text-text-muted hover:text-text transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 overflow-y-auto space-y-4 text-xs flex-1">
          {/* Key Name */}
          <div>
            <label className="font-semibold text-text block mb-1.5">
              {t('apikeys.keyNameLabel')} <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('apikeys.keyNamePlaceholder')}
              className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 text-xs"
            />
          </div>

          {/* Environment Mode */}
          <div>
            <label className="font-semibold text-text block mb-1.5">
              {t('apikeys.environmentLabel')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setEnvironment('live')}
                className={`py-2 px-3 rounded-xl border text-center transition-all ${
                  environment === 'live'
                    ? 'bg-primary/10 border-primary text-primary font-bold shadow-xs'
                    : 'border-border hover:bg-surface-subtle text-text'
                }`}
              >
                {t('apikeys.envLive')}
              </button>
              <button
                type="button"
                onClick={() => setEnvironment('test')}
                className={`py-2 px-3 rounded-xl border text-center transition-all ${
                  environment === 'test'
                    ? 'bg-amber-500/10 border-amber-500 text-amber-500 font-bold shadow-xs'
                    : 'border-border hover:bg-surface-subtle text-text'
                }`}
              >
                {t('apikeys.envTest')}
              </button>
            </div>
          </div>

          {/* Scopes Matrix */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-semibold text-text flex items-center gap-1.5">
                <ShieldCheck className="size-3.5 text-primary" />
                <span>{t('apikeys.scopesLabel')}</span>
              </label>

              <button
                type="button"
                onClick={toggleFullAdmin}
                className={`px-2 py-0.5 rounded text-[11px] font-semibold border transition-colors ${
                  isFullAdmin
                    ? 'bg-rose-500/10 border-rose-500/30 text-rose-500'
                    : 'border-border hover:bg-surface-subtle text-text-muted'
                }`}
              >
                {t('apikeys.scopeAll')}
              </button>
            </div>

            <div className="space-y-1.5 rounded-xl border border-border bg-surface-subtle p-3 max-h-48 overflow-y-auto">
              {AVAILABLE_SCOPES.map((scope) => {
                const checked = isFullAdmin || selectedScopes.includes(scope.id);
                return (
                  <label
                    key={scope.id}
                    className="flex items-center gap-2.5 p-1.5 rounded-lg hover:bg-surface cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={isFullAdmin}
                      onChange={() => toggleScope(scope.id)}
                      className="rounded border-border text-primary focus:ring-primary"
                    />
                    <span className="font-mono text-[11px] text-text">{scope.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Rate Limit RPM */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="font-semibold text-text flex items-center gap-1.5">
                <Zap className="size-3.5 text-amber-500" />
                <span>{t('apikeys.rateLimitLabel')}</span>
              </label>
              <div className="flex items-center gap-1">
                {[60, 120, 300, 600].map((rpm) => (
                  <button
                    key={rpm}
                    type="button"
                    onClick={() => setRateLimitRpm(rpm)}
                    className={`px-2 py-0.5 rounded text-[10px] font-mono transition-colors ${
                      rateLimitRpm === rpm
                        ? 'bg-primary text-primary-foreground font-bold'
                        : 'border border-border text-text-muted hover:text-text'
                    }`}
                  >
                    {rpm} RPM
                  </button>
                ))}
              </div>
            </div>
            <input
              type="number"
              min="1"
              max="2000"
              value={rateLimitRpm}
              onChange={(e) => setRateLimitRpm(parseInt(e.target.value, 10) || 60)}
              className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-text font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 text-xs"
            />
          </div>

          {/* Monthly Spend Limit */}
          <div>
            <label className="font-semibold text-text flex items-center gap-1.5 mb-1.5">
              <DollarSign className="size-3.5 text-emerald-500" />
              <span>{t('apikeys.spendLimitLabel')}</span>
            </label>
            <input
              type="number"
              min="1"
              step="100"
              value={monthlySpendLimit}
              onChange={(e) => setMonthlySpendLimit(e.target.value)}
              placeholder={t('apikeys.spendLimitPlaceholder')}
              className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-text font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 text-xs"
            />
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => setCreateModalOpen(false)}
              className="px-4 py-2 text-xs font-medium rounded-xl border border-border hover:bg-surface text-text transition-colors"
            >
              {t('apikeys.cancelBtn')}
            </button>
            <button
              type="submit"
              disabled={!name.trim() || isSubmitting}
              className="px-4 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-all flex items-center gap-1.5 shadow-sm"
            >
              <Plus className="size-4" />
              <span>{t('apikeys.createBtn')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
